(() => {
  'use strict';

  if (window.NoyafaBrandHero) {
    window.NoyafaBrandHero.initAll();
    return;
  }

  const controllers = new Map();
  // Keep the branded experience consistent across OS-level motion preferences.
  const reducedMotion = { matches: false };

  function createController(root) {
    const logoStage = root.querySelector('[data-nbh-logo-stage]');
    const logoFace = root.querySelector('.nbh-logo-face');
    const signalSvg = root.querySelector('.nbh-signal-field svg');
    const scrollCue = root.querySelector('[data-nbh-scroll-cue]');
    const sectionWrapper = root.closest('.shopify-section');
    const headerWrapper = document.getElementById('HeaderWrapper');
    const abortController = new AbortController();
    const listenerOptions = { signal: abortController.signal };
    let resizeObserver;
    let bodyObserver;
    let offsetFrame = 0;
    let offsetTimeout = 0;
    let scrollFrame = 0;
    let revealFrame = 0;
    let revealRun = 0;

    if (!logoStage || !logoFace) {
      return { destroy() {} };
    }

    function syncHeaderOffset() {
      const sectionTop = sectionWrapper
        ? sectionWrapper.getBoundingClientRect().top + window.scrollY
        : root.getBoundingClientRect().top + window.scrollY;
      const viewportOffset = Math.max(0, Math.round(sectionTop));
      const mobileDock = document.querySelector('.mobile-menu-tabbar');
      const mobileDockStyle = mobileDock ? window.getComputedStyle(mobileDock) : null;
      const mobileDockHeight = mobileDock &&
          mobileDockStyle &&
          mobileDockStyle.position === 'fixed' &&
          mobileDockStyle.display !== 'none'
        ? Math.max(0, Math.round(mobileDock.getBoundingClientRect().height))
        : 0;
      root.style.setProperty('--nbh-header-offset', `${viewportOffset}px`);
      root.style.setProperty('--nbh-mobile-dock-offset', `${mobileDockHeight}px`);
    }

    function requestOffsetSync() {
      if (offsetFrame) cancelAnimationFrame(offsetFrame);
      offsetFrame = requestAnimationFrame(() => {
        offsetFrame = requestAnimationFrame(() => {
          offsetFrame = 0;
          syncHeaderOffset();
        });
      });

      window.clearTimeout(offsetTimeout);
      offsetTimeout = window.setTimeout(syncHeaderOffset, 250);
    }

    function resetSignalTimeline() {
      if (signalSvg && typeof signalSvg.setCurrentTime === 'function') {
        signalSvg.setCurrentTime(0);
      }
    }

    function setRevealState(stageProgress, logoProgress) {
      const stageEase = 1 - Math.pow(1 - stageProgress, 3);
      const logoEase = logoProgress < 0.5
        ? 4 * logoProgress * logoProgress * logoProgress
        : 1 - Math.pow(-2 * logoProgress + 2, 3) / 2;
      const logoOpacity = Math.min(1, logoProgress / 0.24);

      root.style.setProperty('--nbh-stage-opacity', stageEase.toFixed(4));
      root.style.setProperty('--nbh-stage-scale', (1.012 - stageEase * 0.012).toFixed(5));
      root.style.setProperty('--nbh-logo-opacity', logoOpacity.toFixed(4));
      root.style.setProperty('--nbh-logo-clip', `${(100 - logoEase * 100).toFixed(3)}%`);
    }

    function startReveal() {
      const currentRun = ++revealRun;
      if (revealFrame) cancelAnimationFrame(revealFrame);

      root.classList.remove('is-reveal-complete');
      root.classList.add('is-frame-driven');
      setRevealState(0, 0);

      if (!root.classList.contains('is-ready')) {
        root.classList.add('is-ready');
      }

      if (reducedMotion.matches) {
        setRevealState(1, 1);
        root.classList.add('is-reveal-complete');
        root.classList.remove('is-frame-driven');
        return;
      }

      let elapsed = 0;
      let previousTime = performance.now();

      function renderFrame(now) {
        if (currentRun !== revealRun || abortController.signal.aborted) return;

        // Cap elapsed progress so a busy Shopify/app frame cannot skip the reveal.
        elapsed += Math.min(24, Math.max(0, now - previousTime));
        previousTime = now;

        const stageProgress = Math.min(1, elapsed / 900);
        const logoProgress = Math.min(1, Math.max(0, elapsed - 120) / 1760);
        setRevealState(stageProgress, logoProgress);

        if (stageProgress < 1 || logoProgress < 1) {
          revealFrame = requestAnimationFrame(renderFrame);
          return;
        }

        revealFrame = 0;
        root.classList.add('is-reveal-complete');
        root.classList.remove('is-frame-driven');
      }

      revealFrame = requestAnimationFrame(renderFrame);
    }

    function replay() {
      root.classList.remove('is-ready');
      root.classList.remove('is-reveal-complete');
      root.classList.add('is-frame-driven');
      setRevealState(0, 0);
      resetSignalTimeline();
      void root.offsetWidth;
      requestAnimationFrame(startReveal);
    }

    function scrollToNextSection() {
      const rootBottom = root.getBoundingClientRect().bottom + window.scrollY;
      let nextSection = sectionWrapper ? sectionWrapper.nextElementSibling : null;

      while (nextSection) {
        const nextRect = nextSection.getBoundingClientRect();
        if (nextRect.height > 24) break;
        nextSection = nextSection.nextElementSibling;
      }

      const targetTop = nextSection
        ? nextSection.getBoundingClientRect().top + window.scrollY
        : rootBottom;
      const headerHeight = headerWrapper
        ? Math.max(0, Math.round(headerWrapper.getBoundingClientRect().height))
        : 0;

      window.scrollTo({
        top: Math.max(0, targetTop - headerHeight),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    function updateScrollCue() {
      scrollFrame = 0;
      if (!scrollCue) return;
      const rect = root.getBoundingClientRect();
      const hideThreshold = Math.max(120, window.innerHeight * 0.44);
      scrollCue.classList.toggle('is-hidden', rect.bottom < hideThreshold);
    }

    function requestScrollCueUpdate() {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(updateScrollCue);
    }

    if (root.dataset.replayOnClick === 'true') {
      logoStage.addEventListener('click', replay, listenerOptions);
    }

    if (scrollCue) {
      scrollCue.addEventListener('click', scrollToNextSection, listenerOptions);
      window.addEventListener('scroll', requestScrollCueUpdate, {
        passive: true,
        signal: abortController.signal
      });
    }

    window.addEventListener('resize', syncHeaderOffset, listenerOptions);
    window.addEventListener('orientationchange', syncHeaderOffset, listenerOptions);

    if ('ResizeObserver' in window && headerWrapper) {
      resizeObserver = new ResizeObserver(syncHeaderOffset);
      resizeObserver.observe(headerWrapper);
    }

    if ('MutationObserver' in window && document.body) {
      bodyObserver = new MutationObserver(requestOffsetSync);
      bodyObserver.observe(document.body, { childList: true });
    }

    syncHeaderOffset();
    requestOffsetSync();
    updateScrollCue();

    if (reducedMotion.matches &&
        signalSvg &&
        typeof signalSvg.pauseAnimations === 'function') {
      signalSvg.pauseAnimations();
    }

    const logoReady = typeof logoFace.decode === 'function'
      ? logoFace.decode().catch(() => undefined)
      : Promise.resolve();
    const timeout = new Promise((resolve) => window.setTimeout(resolve, 8000));

    Promise.race([logoReady, timeout]).then(() => {
      if (!abortController.signal.aborted) {
        requestAnimationFrame(startReveal);
      }
    });

    return {
      replay,
      destroy() {
        abortController.abort();
        if (offsetFrame) cancelAnimationFrame(offsetFrame);
        window.clearTimeout(offsetTimeout);
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        if (revealFrame) cancelAnimationFrame(revealFrame);
        revealRun += 1;
        if (resizeObserver) resizeObserver.disconnect();
        if (bodyObserver) bodyObserver.disconnect();
        root.classList.remove('is-ready', 'is-frame-driven', 'is-reveal-complete');
      }
    };
  }

  function init(root) {
    if (!root || controllers.has(root)) return;
    controllers.set(root, createController(root));
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-noyafa-brand-hero]').forEach(init);
  }

  function findHero(eventTarget) {
    if (!eventTarget || !eventTarget.querySelector) return null;
    return eventTarget.matches('[data-noyafa-brand-hero]')
      ? eventTarget
      : eventTarget.querySelector('[data-noyafa-brand-hero]');
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(findHero(event.target));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = findHero(event.target);
    const controller = root && controllers.get(root);
    if (controller) controller.destroy();
    if (root) controllers.delete(root);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const root = findHero(event.target);
    const controller = root && controllers.get(root);
    if (controller) controller.replay();
  });

  window.NoyafaBrandHero = { init, initAll };

  initAll();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  }
})();
